import {
  ChangeDetectionStrategy,
  Component,
  TemplateRef,
  computed,
  input,
  model,
  output,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { ChevronDownIconComponent } from '@kuiper/icons';
import { TableComponent } from '../table/table.component';
import {
  ParentTableColumns,
  TableColumns,
  TableFilterTab,
} from '../table/table.model';
import { TREE_TABLE_INDENT_PX, TreeTableRow } from './tree-table.model';

@Component({
  selector: 'lib-tree-table',
  imports: [
    NgTemplateOutlet,
    TranslocoPipe,
    ChevronDownIconComponent,
    TableComponent,
  ],
  templateUrl: './tree-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreeTableComponent<T extends TreeTableRow> {
  readonly id = input.required<string>();
  readonly columns = input.required<TableColumns[]>();
  readonly dataSource = input.required<T[]>();
  readonly labelTemplate = input.required<TemplateRef<unknown>>();
  readonly cellTemplates = input<TemplateRef<unknown>[]>([]);
  readonly tableClass = input('');
  readonly loading = input(false);
  readonly parentColumns = input<ParentTableColumns[]>([]);
  readonly enableRowClick = input(true);
  readonly viewCount = input(0);
  readonly enableFilterRouting = input(false);
  readonly tableFilterTabs = input<TableFilterTab[]>([]);
  readonly allowViewToggle = input(false);
  readonly isTableExportable = input(false);
  readonly emptyStateTemplateRef = input<TemplateRef<unknown> | null>(null);
  readonly tableActionSectionRef = input<TemplateRef<unknown> | null>(null);
  /** Search shown at the start of the Tables_new_design toolbar. */
  readonly searchTemplateRef = input<TemplateRef<unknown> | null>(null);
  readonly filterDropdownTemplateRef = input<TemplateRef<unknown> | null>(null);
  readonly isCalendarViewEnabled = input(false);
  readonly calendarTemplateRef = input<TemplateRef<unknown> | null>(null);
  readonly galleryCardTemplateRef = input<TemplateRef<unknown> | null>(null);
  readonly galleryClass = input(
    'w-full gap-lg grid grid-cols-[repeat(auto-fill,minmax(15.25rem,1fr))] mt-2xl'
  );
  readonly lastColumnAlignment = input('');
  readonly rowTestIdPrefix = input('tree-table-row');
  readonly toggleTestIdPrefix = input('tree-table-toggle');
  readonly expandAriaLabel = input('tree_table_expand_button');
  readonly collapseAriaLabel = input('tree_table_collapse_button');

  readonly isGalleryView = model(false);
  readonly isCalendarView = model(false);

  readonly toggleExpanded = output<T['id']>();
  readonly handleRowClick = output<T>();
  readonly handleSortColumn = output<TableColumns>();
  readonly handleTableFilterTab = output<TableFilterTab>();
  readonly handleViewIconClick = output<unknown>();
  readonly submitTableExport = output<FormGroup>();

  private readonly treeCell =
    viewChild<TemplateRef<unknown>>('treeCell');

  protected readonly indentPx = TREE_TABLE_INDENT_PX;

  protected readonly bodyTemplates = computed(() => {
    const treeCell = this.treeCell();
    const cells = this.cellTemplates().map(template => ({ template }));
    return treeCell ? [{ template: treeCell }, ...cells] : cells;
  });

  protected readonly rowClassFn = computed(
    () =>
      (row: unknown): string => {
        const treeRow = row as T;
        return treeRow.isMatch === false
          ? 'text-neutral-400'
          : 'text-neutral-900';
      }
  );

  protected readonly rowTestIdFn = computed(
    () =>
      (row: unknown): string => {
        const treeRow = row as T;
        return `${this.rowTestIdPrefix()}-${treeRow.id}`;
      }
  );

  protected toggleRow(event: Event, row: T): void {
    event.stopPropagation();
    this.toggleExpanded.emit(row.id);
  }
}
